import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { X, Camera, Keyboard, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Beep sound function
const playBeep = () => {
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 1200;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
    } catch (err) {
        console.log('Beep not supported');
    }
};

export const BarcodeScanner = ({ isOpen, onClose, onScan, forceMode = null, keepOpen = false, title = null, lowZIndex = false, inputRef: externalInputRef = null, inline = false }) => {
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const internalInputRef = useRef(null);
    const inputRef = externalInputRef || internalInputRef;
    const lastScanTime = useRef(0);
    const [manualMode, setManualMode] = useState(false);
    const [manualBarcode, setManualBarcode] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [debugInfo, setDebugInfo] = useState('');
    const [cooldown, setCooldown] = useState(false);
    
    // Detect if mobile device - improved detection
    const isMobileDevice = typeof window !== 'undefined' && (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0)
    );
    
    // Determine mode: forceMode overrides auto-detection
    // forceMode: 'camera' | 'scanner' | null (auto)
    const useCamera = forceMode === 'camera' || (forceMode === null && isMobileDevice);
    const useScanner = !useCamera; // Simplified: if not camera, use scanner
    
    // On PC/scanner mode, auto-focus input
    useEffect(() => {
        if (isOpen && useScanner && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen, useScanner]);

    // Re-focus input when coming back from dialog (lowZIndex changes from true to false)
    useEffect(() => {
        if (isOpen && useScanner && !lowZIndex && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 150);
        }
    }, [lowZIndex, isOpen, useScanner]);

    // Stop scanning
    const stopScanning = () => {
        if (readerRef.current) {
            try {
                readerRef.current.reset();
            } catch (e) {}
            readerRef.current = null;
        }
        setStatus('idle');
    };

    // Start camera and scanning using zxing's built-in method
    const startScanning = async () => {
        setError(null);
        setStatus('requesting');
        setDebugInfo('Pornire cameră...');
        
        // Check if mediaDevices is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setError('Camera nu este suportată în acest browser');
            setDebugInfo('mediaDevices indisponibil');
            setStatus('error');
            return;
        }
        
        try {
            if (!readerRef.current) {
                readerRef.current = new BrowserMultiFormatReader();
            }
            
            setStatus('active');
            setDebugInfo('Căutare cameră...');
            
            // Decode callback with cooldown
            const decodeCallback = (result, error) => {
                if (result) {
                    // Check cooldown - ignore if within 3 seconds of last scan
                    const now = Date.now();
                    if (now - lastScanTime.current < 3000) {
                        return;
                    }
                    lastScanTime.current = now;
                    
                    const code = result.getText();
                    console.log('✅ BARCODE:', code);
                    setDebugInfo('GĂSIT: ' + code);
                    playBeep();
                    onScan(code);
                    
                    if (!keepOpen) {
                        stopScanning();
                        onClose();
                    } else {
                        // Show cooldown indicator for 3 seconds
                        setCooldown(true);
                        setTimeout(() => setCooldown(false), 3000);
                    }
                }
            };
            
            // Try different constraints for zxing - from most specific to least
            const zxingConstraints = [
                { video: { facingMode: 'environment' } },
                { video: { facingMode: { ideal: 'environment' } } },
                { video: true }
            ];
            
            let started = false;
            for (const constraints of zxingConstraints) {
                try {
                    setDebugInfo(`Încercare: ${JSON.stringify(constraints.video.facingMode || 'any')}`);
                    await readerRef.current.decodeFromConstraints(
                        constraints,
                        videoRef.current,
                        decodeCallback
                    );
                    started = true;
                    setDebugInfo('Scanare activă!');
                    break;
                } catch (e) {
                    console.log('ZXing constraint failed:', constraints, e.message);
                    continue;
                }
            }
            
            if (!started) {
                throw new Error('Nu s-a putut porni camera');
            }
            
        } catch (err) {
            console.error('Camera error:', err);
            let errorMessage = err.message;
            
            // Provide user-friendly error messages
            if (err.name === 'NotAllowedError' || err.message.includes('denied') || err.message.includes('not allowed')) {
                errorMessage = 'Acces la cameră refuzat. Permite accesul în setările browserului.';
            } else if (err.name === 'NotFoundError') {
                errorMessage = 'Nu s-a găsit nicio cameră.';
            } else if (err.name === 'NotReadableError') {
                errorMessage = 'Camera este folosită de altă aplicație.';
            }
            
            setError(errorMessage);
            setDebugInfo('Eroare: ' + err.name);
            setStatus('error');
        }
    };

    // Start when modal opens - only in camera mode
    useEffect(() => {
        if (isOpen && !manualMode && useCamera) {
            startScanning();
        }
        return () => {
            stopScanning();
        };
    }, [isOpen, manualMode, useCamera]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setManualMode(false);
            setManualBarcode('');
            setStatus('idle');
            setError(null);
            setDebugInfo('');
            stopScanning();
        }
    }, [isOpen]);

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualBarcode.trim()) {
            playBeep();
            onScan(manualBarcode.trim());
            setManualBarcode('');
            if (!keepOpen) {
                stopScanning();
                onClose();
            } else {
                // Keep open, just clear input and refocus
                setTimeout(() => inputRef.current?.focus(), 100);
            }
        }
    };
    
    // Handle scanner input on PC (scanner types and presses Enter)
    const handleScannerInput = (e) => {
        if (e.key === 'Enter' && manualBarcode.trim()) {
            e.preventDefault();
            playBeep();
            onScan(manualBarcode.trim());
            setManualBarcode('');
            if (!keepOpen) {
                onClose();
            } else {
                // No cooldown on PC - just refocus immediately
                setTimeout(() => inputRef.current?.focus(), 50);
            }
        }
    };

    const handleClose = () => {
        stopScanning();
        onClose();
    };

    if (!isOpen) return null;
    
    // Scanner mode (PC) - Simple input for physical scanner
    if (useScanner) {
        // INLINE MODE: render doar input fără overlay
        if (inline) {
            return (
                <div className="w-full">
                    <Input
                        ref={inputRef}
                        type="text"
                        value={manualBarcode}
                        onChange={(e) => setManualBarcode(e.target.value)}
                        onKeyDown={handleScannerInput}
                        placeholder="Scanează sau tastează codul..."
                        className="bg-white border-zinc-300 text-zinc-900 text-center h-12"
                        autoFocus
                    />
                </div>
            );
        }
        
        // OVERLAY MODE: full-screen modal
        return (
            <div className="scanner-overlay flex items-center justify-center p-4" style={lowZIndex ? { zIndex: 50 } : {}}>
                <div className="w-full max-w-md bg-zinc-900 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-white text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Scanează cu Scanner
                        </h2>
                        <button onClick={handleClose} className="text-white/80 hover:text-white p-2">
                            <X size={24} />
                        </button>
                    </div>
                    
                    <div className="space-y-4">
                        <p className="text-zinc-400 text-sm text-center">
                            Folosește scanner-ul fizic sau tastează codul manual
                        </p>
                        
                        <Input
                            ref={inputRef}
                            type="text"
                            value={manualBarcode}
                            onChange={(e) => setManualBarcode(e.target.value)}
                            onKeyDown={handleScannerInput}
                            placeholder="Scanează sau tastează codul..."
                            className="bg-zinc-800 border-zinc-700 text-white text-center text-xl h-14"
                            autoFocus
                        />
                        
                        <div className="flex gap-3">
                            <Button 
                                variant="outline" 
                                onClick={handleClose}
                                className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                            >
                                Renunță
                            </Button>
                            <Button 
                                onClick={() => {
                                    if (manualBarcode.trim()) {
                                        playBeep();
                                        onScan(manualBarcode.trim());
                                        setManualBarcode('');
                                        if (!keepOpen) {
                                            onClose();
                                        } else {
                                            // No cooldown on PC - just refocus
                                            setTimeout(() => inputRef.current?.focus(), 50);
                                        }
                                    }
                                }}
                                disabled={!manualBarcode.trim()}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                                <CheckCircle size={18} className="mr-2" />
                                Confirmă
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    // Camera mode (Mobile) - Camera with manual option

    return (
        <div className="scanner-overlay flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {manualMode ? 'Introducere Manuală' : 'Scanează Cod Bare'}
                    </h2>
                    <button onClick={handleClose} className="text-white/80 hover:text-white p-2">
                        <X size={24} />
                    </button>
                </div>

                {manualMode ? (
                    <form onSubmit={handleManualSubmit} className="space-y-4">
                        <Input
                            type="text"
                            value={manualBarcode}
                            onChange={(e) => setManualBarcode(e.target.value)}
                            placeholder="Introdu codul de bare..."
                            className="h-14 text-lg bg-white"
                            autoFocus
                        />
                        <Button type="submit" className="w-full h-12 bg-[#E11D48] hover:bg-[#BE123C] text-white">
                            Caută Produs
                        </Button>
                    </form>
                ) : (
                    <div className="relative">
                        <div className="rounded-lg overflow-hidden bg-zinc-900 aspect-[4/3] flex items-center justify-center">
                            <video
                                ref={videoRef}
                                className="w-full h-full object-cover"
                                style={{ display: status === 'active' ? 'block' : 'none' }}
                            />
                            
                            {status === 'requesting' && (
                                <div className="flex flex-col items-center justify-center">
                                    <RefreshCw size={40} className="text-white animate-spin mb-3" />
                                    <p className="text-white text-sm">Se pornește camera...</p>
                                </div>
                            )}
                            
                            {status === 'error' && (
                                <div className="flex flex-col items-center justify-center p-4">
                                    <AlertCircle size={40} className="text-red-400 mb-3" />
                                    <p className="text-red-400 text-sm text-center mb-2">{error}</p>
                                    <p className="text-white/60 text-xs text-center mb-4">
                                        Dacă ai refuzat permisiunea, mergi în setările browserului → Site-uri → Permisiuni cameră și permite accesul.
                                    </p>
                                    <div className="flex gap-2">
                                        <Button onClick={startScanning} variant="outline" className="text-white border-white/30">
                                            <RefreshCw size={16} className="mr-2" />
                                            Reîncearcă
                                        </Button>
                                        <Button 
                                            onClick={() => setManualMode(true)} 
                                            variant="outline" 
                                            className="text-white border-white/30"
                                        >
                                            <Keyboard size={16} className="mr-2" />
                                            Manual
                                        </Button>
                                    </div>
                                </div>
                            )}
                            
                            {status === 'active' && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="scanner-reticle w-64 h-32 rounded-lg"></div>
                                </div>
                            )}
                        </div>
                        
                        {/* Debug info */}
                        <div className="mt-2 p-2 bg-black/50 rounded text-xs text-white/80">
                            <p>Status: {status}</p>
                            <p>{debugInfo}</p>
                        </div>
                        
                        {status === 'active' && (
                            <p className="text-white/60 text-sm mt-2 text-center">
                                Îndreaptă camera spre codul de bare
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (!manualMode) stopScanning();
                            setManualMode(!manualMode);
                        }}
                        className="w-full h-12 bg-white/10 border-white/20 text-white hover:bg-white/20"
                    >
                        {manualMode ? (
                            <><Camera size={20} className="mr-2" />Scanează cu Camera</>
                        ) : (
                            <><Keyboard size={20} className="mr-2" />Introducere Manuală</>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BarcodeScanner;
